import { motion } from 'framer-motion';
import { MessageCircle, Copy, Check, User, QrCode } from 'lucide-react';
import { useState } from 'react';
import { useSiteConfig } from '../../hooks/useSiteConfig';
import { openWhatsApp } from '../../utils/whatsapp';

export default function ContactSection() {
  const siteConfig = useSiteConfig();
  const { contact } = siteConfig;
  const [ctaLabel, setCtaLabel] = useState('📲 立即 WhatsApp 諮詢');
  const [copied, setCopied] = useState<string | null>(null);

  const handleCta = () => {
    setCtaLabel('⏳ 正在開啟 WhatsApp...');
    openWhatsApp(contact.whatsapp, contact.defaultMessage);
  };

  const handleCopy = (value: string, key: string) => {
    navigator.clipboard?.writeText(value).catch(() => {});
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-deep-black relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-neon-purple/15 rounded-full blur-3xl" />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-3xl md:text-4xl font-bold text-white mb-4"
        >
          {contact.sectionTitle}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-gray-400 mb-10"
        >
          {contact.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="neon-card rounded-3xl p-8 md:p-10 mb-8"
        >
          <div className="grid sm:grid-cols-2 gap-6 mb-8 text-left">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-neon-cyan/10 flex items-center justify-center flex-shrink-0">
                <User size={18} className="text-neon-cyan" />
              </div>
              <div>
                <h4 className="text-xs text-gray-500 mb-1">對接顧問</h4>
                <p className="text-white font-medium">{contact.consultant}</p>
              </div>
            </div>

            <button
              onClick={() => handleCopy(contact.whatsappDisplay, 'whatsapp')}
              className="flex items-start gap-3 text-left group"
            >
              <div className="w-10 h-10 rounded-xl bg-neon-cyan/10 flex items-center justify-center flex-shrink-0">
                <MessageCircle size={18} className="text-neon-cyan" />
              </div>
              <div>
                <h4 className="text-xs text-gray-500 mb-1">WhatsApp（長按複製）</h4>
                <p className="text-white font-medium flex items-center gap-1.5">
                  {contact.whatsappDisplay}
                  {copied === 'whatsapp' ? (
                    <Check size={14} className="text-neon-cyan" />
                  ) : (
                    <Copy size={14} className="text-gray-500 group-hover:text-neon-cyan transition-colors" />
                  )}
                </p>
              </div>
            </button>

            <button
              onClick={() => handleCopy(contact.wechat, 'wechat')}
              className="flex items-start gap-3 text-left group"
            >
              <div className="w-10 h-10 rounded-xl bg-neon-purple/10 flex items-center justify-center flex-shrink-0">
                <QrCode size={18} className="text-neon-purple" />
              </div>
              <div>
                <h4 className="text-xs text-gray-500 mb-1">WeChat（長按複製）</h4>
                <p className="text-white font-medium flex items-center gap-1.5">
                  {contact.wechat}
                  {copied === 'wechat' ? (
                    <Check size={14} className="text-neon-purple" />
                  ) : (
                    <Copy size={14} className="text-gray-500 group-hover:text-neon-purple transition-colors" />
                  )}
                </p>
              </div>
            </button>

            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-neon-purple/10 flex items-center justify-center flex-shrink-0">
                <User size={18} className="text-neon-purple" />
              </div>
              <div>
                <h4 className="text-xs text-gray-500 mb-1">公司名稱</h4>
                <p className="text-white font-medium">{contact.companyName}</p>
                <p className="text-gray-500 text-xs">{contact.companyNameEn}</p>
              </div>
            </div>
          </div>

          {contact.qrCode && (
            <div className="mb-8 flex flex-col items-center">
              <p className="text-sm text-gray-400 mb-4">掃描 QR Code 直接 WhatsApp</p>
              <div className="p-3 bg-white rounded-2xl shadow-lg">
                <img
                  src={contact.qrCode}
                  alt="WhatsApp QR Code"
                  className="w-44 h-44 object-contain"
                />
              </div>
            </div>
          )}

          <button
            onClick={handleCta}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-neon-cyan via-neon-blue to-neon-purple text-deep-navy font-bold rounded-xl transition-all duration-300 hover:brightness-110 animate-pulse-glow"
          >
            <MessageCircle size={18} />
            {ctaLabel}
          </button>
        </motion.div>
      </div>
    </section>
  );
}
